// eslint-disable-next-line no-shadow
enum PaymentErrorCodes {
  AuthError = "111",
  MaxAmountPerTransactionReached = "114",
  MaxVolumeReached = "113",
  Unknown = "000",
  UnsupportedPaymentMethod = "115",
  UnverifiedPaymentMethod = "112",
}

// eslint-disable-next-line no-shadow
enum FormattedErrorMessage {
  CardExpired = "Transaction declined. The card provided is expired. Please use a different payment method and try again.",
  Declined = "Transaction declined. Please check with your payment method issuer for more details or try using a different payment method.",
  Default = "An unexpected error occured while processing your request. Please try again later or use a different payment method.",
  InsufficientFunds = "Transaction declined. The card used has insufficient funds. Please check with your card issuer for more details or try again using a different payment method.",
  InvalidCardNumber = "Transaction declined. The card number provided is incorrect. Please review your card information and try again or use a different payment method.",
  InvalidRoutingNumber = "Transaction declined. The routing number provided must have 9 digits. Please review your bank account details and try again or use a different payment method.",
  InvalidExpiryMonth = "Transaction declined. The card expiration month provided is invalid. Please review your card information and try again or use a different payment method.",
  InvalidExpiryYear = "Transaction declined. The card expiration year provided is invalid. Please review your card information and try again or use a different payment method.",
  InvalidIBAN = "Transaction declined. The IBAN provided is invalid. Please review your bank account details and try again or use a different payment method.",
  InvalidPaymentMethod = "There was an issue with your selected payment method. Please remove this payment method from your account and try to add it again.",
  InvalidSecurityCode = "Transaction declined. The security code entered is invalid. Please review your card details and try again or use a different payment method.",
  MaxDeclinedReach = "Transaction declined. The card has exceeded the maximum number of declined payment attempts in the last 24-hour period. Please check with your card issuer for more details or try again using a different payment method.",
  NotAuthorizedForDebitPayments = "Transaction declined. The bank account provided is not authorized for debit payments. Please use a different payment method and try again.",
  PaymentFailed = "The payment attempt failed. Please try again or use a different payment method. If the problem persists, contact your payment provider for assistance.",
  PurchaseNotSupported = "Transaction declined. The card used does not support this type of purchase. Please check with your card issuer for more details or try again using a different payment method.",
  UnknownRoutingNumber = "Transaction declined. The routing number provided is invalid. Please review your bank account details and try again.",
  TooManyAttempts = "Transaction declined. The card has reached the limit for repeated payment attempts. Please check with your card issuer for more details or try again using a different payment method.",
}

interface FormattedError {
  regex: RegExp;
  message: FormattedErrorMessage;
  code?: string;
}

const internalErrorsMap: FormattedError[] = [
  {
    regex: /requires authentication/i,
    message: FormattedErrorMessage.Default,
    code: PaymentErrorCodes.AuthError,
  },
  {
    regex: /not verified/i,
    message: FormattedErrorMessage.Default,
    code: PaymentErrorCodes.UnverifiedPaymentMethod,
  },
  {
    regex: /exceeded\s+.*?volume\s+.*?limit/i,
    message: FormattedErrorMessage.Default,
    code: PaymentErrorCodes.MaxVolumeReached,
  },
  {
    regex: /does not support\s+.*?payment\s+.*amounts\s+.*?greater\s+.*?than/i,
    message: FormattedErrorMessage.Default,
    code: PaymentErrorCodes.MaxAmountPerTransactionReached,
  },
  {
    regex: /payment method\s+.*?is invalid/i,
    message: FormattedErrorMessage.Default,
    code: PaymentErrorCodes.UnsupportedPaymentMethod,
  },
  {
    regex: /payment method\s+.*?needs to be activated/i,
    message: FormattedErrorMessage.Default,
    code: PaymentErrorCodes.UnsupportedPaymentMethod,
  },
  {
    regex: /unexpected error/i,
    message: FormattedErrorMessage.Default,
    code: PaymentErrorCodes.Unknown,
  },
];

const errorsMap: FormattedError[] = [
  {
    regex: /insufficient funds/i,
    message: FormattedErrorMessage.InsufficientFunds,
  },
  {
    regex: /high likelihood of chargeback/i,
    message: FormattedErrorMessage.Declined,
  },
  {
    regex: /too many submissions/i,
    message: FormattedErrorMessage.TooManyAttempts,
  },
  {
    regex: /card_velocity_exceed/i,
    message: FormattedErrorMessage.TooManyAttempts,
  },
  {
    regex: /does not support this type of purchase/i,
    message: FormattedErrorMessage.PurchaseNotSupported,
  },
  {
    regex: /routing number must have 9 digits/i,
    message: FormattedErrorMessage.InvalidRoutingNumber,
  },
  {
    regex: /achBankABACode:ExceededMaxLength/i,
    message: FormattedErrorMessage.InvalidRoutingNumber,
  },
  {
    regex: /routing_number_invalid/i,
    message: FormattedErrorMessage.UnknownRoutingNumber,
  },
  {
    regex: /security code is (invalid|incorrect)/i,
    message: FormattedErrorMessage.InvalidSecurityCode,
  },
  {
    regex: /not authorized for debit payments/i,
    message: FormattedErrorMessage.NotAuthorizedForDebitPayments,
  },
  {
    regex: /card has expired/i,
    message: FormattedErrorMessage.CardExpired,
  },
  {
    regex: /card number is incorrect/i,
    message: FormattedErrorMessage.InvalidCardNumber,
  },
  {
    regex: /expiration month is invalid/i,
    message: FormattedErrorMessage.InvalidExpiryMonth,
  },
  {
    regex: /expiration year is invalid/i,
    message: FormattedErrorMessage.InvalidExpiryYear,
  },
  {
    regex: /payment failed/i,
    message: FormattedErrorMessage.PaymentFailed,
  },
  {
    regex: /exceeded the maximum number of declines/i,
    message: FormattedErrorMessage.MaxDeclinedReach,
  },
  {
    regex: /iban is invalid/i,
    message: FormattedErrorMessage.InvalidIBAN,
  },
  {
    regex: /resource_missing/i,
    message: FormattedErrorMessage.InvalidPaymentMethod,
  },
  {
    regex: /may not be used again/i,
    message: FormattedErrorMessage.InvalidPaymentMethod,
  },
  {
    regex: /cannot be attached/i,
    message: FormattedErrorMessage.InvalidPaymentMethod,
  },
  // internal errors
  ...internalErrorsMap,
  // declined error is common so keep it at the end so that other errors are caught first
  {
    regex: /your card was declined/i,
    message: FormattedErrorMessage.Declined,
  },
];

export function formatPaymentError(errorMsg: string) {
  const error = errorsMap.find(({ regex }) => regex.test(errorMsg));

  return error
    ? {
        message: error.message,
        code: error.code ? ` Error Code ${error.code}` : "",
      }
    : {
        message: FormattedErrorMessage.Default,
        code: ` Error Code ${PaymentErrorCodes.Unknown}`,
      };
}
